import { Link } from "react-router-dom";
import { AppCta } from "../MarketingLayout";

/** Features page: capabilities in depth, benefit-led, grounded in the product. */
export function Features() {
  const groups = [
    {
      eyebrow: "The ledger",
      title: "A trial balance that is always correct",
      cards: [
        {
          icon: "⚖",
          h: "Live tie-out",
          p: "Double-entry is enforced at the point of posting — debits must equal credits — so your trial balance, P&L, and balance sheet reconcile every time you look, not just at month-end.",
        },
        {
          icon: "✎",
          h: "Journal & double-entry",
          p: "Post manual journals or entries from invoices and bills against a UAE-appropriate chart of accounts, with every line categorized as you go.",
        },
        {
          icon: "▤",
          h: "UAE chart of accounts",
          p: "Start from a chart of accounts shaped for UAE businesses instead of building one from scratch or bending a generic template.",
        },
      ],
    },
    {
      eyebrow: "Compliance",
      title: "UAE tax categorization, attached at the source",
      cards: [
        {
          icon: "🏛",
          h: "5% VAT tracking",
          p: "VAT is tracked on each transaction as you post it, so the input/output picture is always current — no reconstructing it at return time.",
        },
        {
          icon: "%",
          h: "Corporate-tax ready",
          p: "Corporate-tax (9%) categorization tags sit on your accounts and entries, keeping your books ready for the categorization the new regime expects.",
        },
        {
          icon: "▧",
          h: "Statements on demand",
          p: "Generate a P&L and balance sheet for a bank, an auditor, or a decision — from books that already tie out — without assembling anything by hand.",
        },
      ],
    },
    {
      eyebrow: "Scale & money",
      title: "Multiple entities, multiple currencies, one truth",
      cards: [
        {
          icon: "◆",
          h: "Multi-entity & consolidation",
          p: "Keep each Free Zone or Mainland entity on its own books, then switch to a consolidated group view to see the whole picture in one place.",
        },
        {
          icon: "$",
          h: "AED base + FX gain/loss",
          p: "Record foreign-currency invoices and bills against your AED base with FX gain/loss handled automatically, so multi-currency doesn’t break the tie-out.",
        },
        {
          icon: "⇌",
          h: "Bank reconciliation",
          p: "Match bank statement lines against ledger entries and clear the difference to zero, so the numbers you report match the numbers in the bank.",
        },
      ],
    },
    {
      eyebrow: "Adopt & control",
      title: "Coexist with your current tool — and control access",
      cards: [
        {
          icon: "⇄",
          h: "Import / export",
          p: "CSV import and export let you run alongside an existing tool and move data both ways, so you can adopt without a risky day-one migration.",
        },
        {
          icon: "👥",
          h: "Roles & access",
          p: "Invite your team with owner, admin, member, and viewer roles — give an external accountant read-only access without handing over the keys.",
        },
        {
          icon: "🔒",
          h: "Secure & tenant-scoped",
          p: "Your ledger is stored on the server, scoped to your organization and enforced server-side — only signed-in members of your org can see it.",
        },
      ],
    },
  ];

  return (
    <>
      <section className="mk-section">
        <div className="mk-eyebrow">Features</div>
        <h1 className="mk-h1">Purpose-built for UAE bookkeeping — not adapted to it.</h1>
        <p className="mk-lead">
          Every capability exists to get you to one outcome: a single source of
          truth that is always tie-out correct, with UAE tax and multi-currency
          handled as you work.
        </p>
      </section>

      {groups.map((g) => (
        <section className="mk-section" key={g.title} style={{ paddingTop: 0 }}>
          <div className="mk-eyebrow">{g.eyebrow}</div>
          <h2 className="mk-h2">{g.title}</h2>
          <div className="mk-grid cols-3">
            {g.cards.map((c) => (
              <div className="mk-card" key={c.h}>
                <div className="mk-ico" aria-hidden="true">
                  {c.icon}
                </div>
                <h3>{c.h}</h3>
                <p>{c.p}</p>
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="mk-cta">
        <div className="mk-section narrow">
          <h2 className="mk-h2">See it against your own numbers.</h2>
          <div className="mk-hero-cta">
            <AppCta className="mk-btn primary lg">Start your free trial</AppCta>
            <Link className="mk-btn secondary lg" to="/pricing">
              Compare plans
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
